import React, { useContext } from 'react';
import styled from 'styled-components';

import OptionSettings from './OptionSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import InputSettingsField from './InputSettingsField';
import CheckboxSettingsField from './CheckboxSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import Button from '../../misc/Button';
import Icon from '../../misc/Icon';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import { StyledFieldWrapper, StyledAccordionFieldWrapper, StyledButtonGroupWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldTargetDisplayTypes from '../../../constants/FieldTargetDisplayTypes';
import FileViews from '../../../constants/FileViews';
import FieldTypes from '../../../constants/FieldTypes';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const FieldSettingsMultiSelectField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,

  activeFieldPosition,
  title,
  tipTitle,
  tipDescription,
  tipVisible,
  type,
  displayType,
  options,
  invertBooleanCondition,
  multi,
  metadata,
  config,
  hidden,
  disabled,
  value,

  targetValue,
  targetValueDisplayType,
  customTrueTargetDisplayValue,
  customFalseTargetDisplayValue,

  //API Developers
  identifier,
  required,

  //Position
  x,
  y,
  width,
  height,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,
  margin,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onOptionCreate,
  onOptionChange,
  onOptionDelete,
}) => {

  const theme = useContext(ThemeContext);

  let nextFieldSettingsIcon = null;
  if (type === FieldTypes.dropdown) nextFieldSettingsIcon = ThemeIcons.fields.dropdown;
  if (type === FieldTypes.multiSelect) nextFieldSettingsIcon = multi ? ThemeIcons.fields.multiSelect : ThemeIcons.fields.multiSelectSingle;

  const isDropdownType = type === FieldTypes.dropdown;
  const isSelectorType = displayType && (displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.horizontal);

  const validOptions = [];
  options.forEach((option) => {
    if (!option.deleted) validOptions.push(option);
  });

  const stylesNode = (
    <>
      <AccordionSettings
        isOpen
        title="Title"
        height={170}
      >
        <FieldStyleSettings
          id={`${fieldId}-title`}
          {...{
            fontSize: titleFontSize,
            onFontSizeChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontSize: value }),

            fontColor: titleFontColor,
            onFontColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontColor: value }),

            textAlign: titleTextAlign,
            onTextAlignChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextAlign: value }),

            fontStyle: titleFontStyle,
            onFontStyleChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontStyle: value }),

            fontWeight: titleFontWeight,
            onFontWeightChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontWeight: value }),

            textTransform: titleTextTransform,
            onTextTransformChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextTransform: value }),

            textDecoration: titleTextDecoration,
            onTextDecorationChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextDecoration: value }),
          }}
        />
      </AccordionSettings>
      <DividerLine />
      <AccordionSettings
        isOpen
        title="Text"
        height={170}
      >
        <FieldStyleSettings
          id={`${fieldId}-field`}
          {...{

            fontSize,
            onFontSizeChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

            fontColor,
            onFontColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

            textAlign,
            onTextAlignChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

            fontStyle,
            onFontStyleChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

            fontWeight,
            onFontWeightChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

            textTransform,
            onTextTransformChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

            textDecoration,
            onTextDecorationChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

            textOverflow,
            onTextOverflowChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textOverflow: value }),

          }}
        />
      </AccordionSettings>
      <DividerLine />
      <AccordionSettings
        isOpen
        title="Field"
        height={340}
      >
        <FieldStyleSettings
          id={`${fieldId}-field`}
          {...{

            borderColor,
            onBorderColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

            borderRadius,
            onBorderRadiusChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

            borderWidth,
            onBorderWidthChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

            backgroundColor,
            onBackgroundColorChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

            padding,
            onPaddingChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

            margin,
            onMarginChange: (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

            zIndex,
            onZIndexChange: (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

          }}
        />
        <PositionSettings
          {...{
            view,
            x,
            y,
            width,
            height,
            onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
          }}
        />
      </AccordionSettings>
    </>
  );

  const settingsNode = (
    <>
      {view === FileViews.mobile ? null : (
        <StyledButtonGroupWrapper>
          <ButtonGroupSettings
            id="displayType"
            types={[
              { type: FieldDisplayTypes.original, icon: ThemeIcons.settings.selectDisplayTypeOriginal, active: displayType === FieldDisplayTypes.original },
              isDropdownType ? null : { type: FieldDisplayTypes.horizontal, icon: ThemeIcons.settings.selectDisplayTypeHorizontal, active: displayType === FieldDisplayTypes.horizontal },
              isDropdownType ? null : { type: FieldDisplayTypes.text, icon: ThemeIcons.settings.selectDisplayTypeText, active: displayType === FieldDisplayTypes.text },
              { type: FieldDisplayTypes.check, icon: ThemeIcons.settings.selectDisplayTypeCheck, active: displayType === FieldDisplayTypes.check },
              { type: FieldDisplayTypes.radio, icon: ThemeIcons.settings.selectDisplayTypeRadio, active: displayType === FieldDisplayTypes.radio },
              { type: FieldDisplayTypes.circle, icon: ThemeIcons.settings.selectDisplayTypeCircle, active: displayType === FieldDisplayTypes.circle },
              { type: FieldDisplayTypes.square, icon: ThemeIcons.settings.selectDisplayTypeSquare, active: displayType === FieldDisplayTypes.square },
            ]}
            onChange={(value) => {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { displayType: value });
            }}
          />
        </StyledButtonGroupWrapper>
      )}
      <SettingsLabel>Title</SettingsLabel>
      <DividerLine style={{ marginBottom: 14 }} />
      <TitleSettings
        {...{
          title,
          titleDisplay
        }}
        onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
      />
      <SettingsLabel>Field</SettingsLabel>
      <DividerLine style={{ marginBottom: 14 }} />
      {isSelectorType || !isDropdownType ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="dropdown"
            fieldId={fieldId}
            label="Default Value"
            value={value}
            options={validOptions}
            onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { value })}
          />
        </StyledFieldWrapper>
      )}
      {!isSelectorType ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="dropdown"
            fieldId={fieldPositionId}
            addonBefore={<Icon icon={ThemeIcons.settings.targetValue} />}
            value={targetValue}
            options={validOptions}
            onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { targetValue: value })}
          />
        </StyledFieldWrapper>
      )}
      <StyledFieldWrapper>
        <OptionSettings
          id="options"
          config={config?.options}
          activeFieldPosition={activeFieldPosition}
          type={type}
          displayType={displayType}
          options={options}
          onChange={(optionId, data) => onOptionChange(fieldId, fieldIdentifier, fieldPositionId, optionId, data)}
          onDelete={(optionId) => onOptionDelete(fieldId, fieldIdentifier, fieldPositionId, optionId)}
          onCreate={(data) => onOptionCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
          targetValue={targetValue}
        />
      </StyledFieldWrapper>
      {isDropdownType ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id="multi"
            icon={ThemeIcons.settings.multi}
            iconColor={multi ? theme.primaryColor : theme.borderColor}
            label="Multiple Choice"
            selected={multi}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { multi: selected },)}
          />
        </StyledFieldWrapper>
      )}
      <StyledFieldWrapper>
        <CheckboxSettingsField
          icon={ThemeIcons.settings.disabled}
          iconColor={disabled ? theme.primaryColor : theme.borderColor}
          id="disabled"
          label="Read-only"
          selected={disabled}
          onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { disabled: selected },)}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <CheckboxSettingsField
          icon={ThemeIcons.settings.required}
          iconColor={required ? theme.dangerColor : theme.borderColor}
          iconSize="1x"
          id="required"
          label="Required"
          selected={required}
          onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { required: selected },)}
        />
      </StyledFieldWrapper>
      <StyledFieldWrapper>
        <CheckboxSettingsField
          icon={ThemeIcons.settings.hide}
          iconColor={hidden ? theme.warningColor : theme.borderColor}
          id="hidden"
          label="Hidden"
          selected={hidden}
          onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
        />
      </StyledFieldWrapper>
      {fields.length < 2 ? null : (
        <>
          <DividerLine />
          <StyledFieldWrapper>
            <ConditionalLogicSettings
              fileId={fileId}
              targetLabel="field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={logic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {!config?.metadata ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={config?.metadata}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      <DividerLine style={{ marginBottom: 0 }} />
      <AccordionSettings
        title="Tooltip"
        height={166}
      >
        <TooltipSettings
          {...{
            tipVisible,
            tipTitle,
            tipDescription
          }}
          onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
        />
      </AccordionSettings>
      <DividerLine style={{ marginBottom: 0 }} />
      <AccordionSettings
        title={config?.identifier?.label || 'Identifier'}
        height={60}
      >
        <IdentifierSelectorSettings
          fieldId={fieldId}
          fieldType={type}
          type="text"
          customValue={config?.identifier?.options}
          enableCustomOption={config?.identifier?.custom}
          value={identifier}
          onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
        />
      </AccordionSettings>
      {isDropdownType || !isSelectorType ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Advanced"
            height={targetValueDisplayType !== FieldTargetDisplayTypes.custom ? 80 : 208}
          >
            <StyledAccordionFieldWrapper>
              <CheckboxSettingsField
                id="invertBooleanCondition"
                label="Invert Boolean Condition"
                selected={invertBooleanCondition}
                onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { invertBooleanCondition: selected })}
              />
            </StyledAccordionFieldWrapper>
            {displayType && displayType !== FieldDisplayTypes.text ? null : (
              <>
                <StyledAccordionFieldWrapper>
                  <CheckboxSettingsField
                    id="targetValueDisplayType"
                    label="Use Custom True/False Text"
                    selected={targetValueDisplayType === FieldTargetDisplayTypes.custom}
                    onChange={(selected) => {
                      if (selected) {
                        onChange(fieldId, fieldIdentifier, fieldPositionId, { targetValueDisplayType: FieldTargetDisplayTypes.custom },);
                      } else {
                        onChange(fieldId, fieldIdentifier, fieldPositionId, { targetValueDisplayType: FieldTargetDisplayTypes.original },);
                      }
                    }}
                  />
                </StyledAccordionFieldWrapper>
                {targetValueDisplayType !== FieldTargetDisplayTypes.custom ? null : (
                  <>
                    <StyledAccordionFieldWrapper>
                      <InputSettingsField
                        inputStyles={{ maxWidth: '120px' }}
                        fieldId={fieldId}
                        label="True Text"
                        type="text"
                        value={customTrueTargetDisplayValue}
                        onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { customTrueTargetDisplayValue: value })}
                      />
                    </StyledAccordionFieldWrapper>
                    <StyledAccordionFieldWrapper>
                      <InputSettingsField
                        inputStyles={{ maxWidth: '120px' }}
                        fieldId={fieldId}
                        label="False Text"
                        type="text"
                        value={customFalseTargetDisplayValue}
                        onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { customFalseTargetDisplayValue: value })}
                      />
                    </StyledAccordionFieldWrapper>
                  </>
                )}
              </>
            )}
          </AccordionSettings>
        </>
      )}
      <DividerLine style={{ marginBottom: 14 }} />
      <StyledButtonsWrapper>
        <Button
          danger
          icon={ThemeIcons.settings.delete}
          text="Delete"
          onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
        />
      </StyledButtonsWrapper>

    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsMultiSelectField;
